import FormController from "./form_controller"
import Logger from '../lib/logger'

/**
 * Customer Form Controller - Extends form controller with customer-specific functionality
 */
export default class extends FormController {
  static targets = [
    "nameField", "emailField", "phoneField", "websiteField", "notesField", 
    "autoSaveStatus", "autoSaveMessage"
  ]
  static values = { 
    ...FormController.values,
    customerId: Number
  }

  connect() {
    super.connect()
    this.entityTypeValue = 'customer'
    Logger.debug('CustomerFormController connecting', { customerId: this.customerIdValue })
  }

  getStorageKey() {
    return this.customerIdValue ? `customer_form_${this.customerIdValue}` : 'customer_form_state'
  }

  // Jumps to and focuses one of this controller's *Field targets, e.g. the
  // "Focus Name Field" quick action, which passes { field: "name" } via
  // data-customer-form-field-param on the triggering button.
  focusField(event) {
    const field = event.params?.field
    if (!field) return

    const capitalized = field.charAt(0).toUpperCase() + field.slice(1)
    const hasTargetProp = `has${capitalized}FieldTarget`
    const targetProp = `${field}FieldTarget`

    if (this[hasTargetProp]) {
      this[targetProp].focus()
      this[targetProp].scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  restoreFieldValues(data) {
    // Restore field values with specific field mapping
    if (data.name && this.hasNameFieldTarget) {
      this.nameFieldTarget.value = data.name
    }
    if (data.email && this.hasEmailFieldTarget) {
      this.emailFieldTarget.value = data.email
    }
    if (data.phone && this.hasPhoneFieldTarget) {
      this.phoneFieldTarget.value = data.phone
    }
    if (data.website && this.hasWebsiteFieldTarget) {
      this.websiteFieldTarget.value = data.website
    }
    if (data.notes && this.hasNotesFieldTarget) {
      this.notesFieldTarget.value = data.notes
    }
  }

  validateForm() {
    let isValid = true
    const errors = []

    // Validate required fields
    if (this.hasNameFieldTarget && !this.nameFieldTarget.value.trim()) {
      errors.push('Customer name is required')
      isValid = false
    }

    // Validate email format if present
    if (this.hasEmailFieldTarget && this.emailFieldTarget.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.emailFieldTarget.value.trim())) {
        errors.push('Please enter a valid email address')
        isValid = false
      }
    }

    // Validate website format if present
    if (this.hasWebsiteFieldTarget && this.websiteFieldTarget.value.trim()) {
      const urlRegex = /^https?:\/\/.+\..+/
      // Was `this.hasWebsiteFieldTarget.value` — hasWebsiteFieldTarget is the boolean
      // presence-check accessor, not the target itself; `.value` on it is undefined, so
      // `.trim()` threw TypeError on every submit with a website value present.
      if (!urlRegex.test(this.websiteFieldTarget.value.trim())) {
        errors.push('Please enter a valid website URL (include http:// or https://)')
        isValid = false
      }
    }

    // Show errors if any
    if (!isValid) {
      this.showErrors(errors)
    }

    return isValid
  }
}
